import { reactive, ref } from "vue";
import api from "@/api";
import { useFormatters } from "./useFormatters";

const { calcularIdade, faixaEtaria } = useFormatters();

/**
 * useClientes
 * Toda a lógica de negócio e chamadas API relacionadas com clientes.
 * O componente ListaClientes.vue só usa este composable — sem axios direto.
 */
export function useClientes() {
  const tableData = ref([]);
  const tableDataOriginal = ref([]);
  const todosProdutos = ref([]);
  const restaurantes = ref([]);
  const filtros_utilizador = ref([]);
  const loading = ref(false);

  const pagination = reactive({
    perPage: 50,
    currentPage: 1,
    perPageOptions: [50, 100, 250, 350, 500],
    total: 0,
  });

  // -----------------------------------
  // Cartões / lista de clientes
  // -----------------------------------
  async function getCartoes({ filtroId = null, clienteForcado = null, searchQuery = null, segmento = null, restaurantesParam = null } = {}) {
    loading.value = true;
    try {
      const { data: res } = await api.get("/cartoes/light", {
        params: {
          page: pagination.currentPage,
          perPage: pagination.perPage,
          filtroId,
          cliente: clienteForcado,
          search: searchQuery || null,
          segmento: segmento || null,
          restaurantes: restaurantesParam ? restaurantesParam.join(",") : null,
        },
      });

      const enriquecidos = (res.data || []).map((c) => {
        const visitas = Array.isArray(c.visitas)
          ? [...c.visitas].sort((a, b) => new Date(b.data_visita) - new Date(a.data_visita))
          : [];

        const idade = c.data_nascimento ? calcularIdade(c.data_nascimento) : null;
        const totalVisitas = c.visitas_total || visitas.length || 0;
        const ticket_medio = totalVisitas > 0 ? (c.gasto_total || c.gasto || 0) / totalVisitas : 0;

        return {
          ...c,
          faixa_etaria: faixaEtaria(idade),
          ticket_medio: Number(ticket_medio.toFixed(2)),
        };
      });

      pagination.total = res.total;
      todosProdutos.value = res.produtos || [];
      tableData.value = enriquecidos;
      tableDataOriginal.value = JSON.parse(JSON.stringify(enriquecidos));

      return enriquecidos;
    } finally {
      loading.value = false;
    }
  }

  async function aplicarFiltros(filtros, page, perPage) {
    loading.value = true;
    try {
      const { data: res } = await api.post("/cartoes/light/filtrar", {
        filtros,
        page: page ?? pagination.currentPage,
        perPage: perPage ?? pagination.perPage,
      });

      pagination.total = res.total;
      tableData.value = res.data;
      tableDataOriginal.value = JSON.parse(JSON.stringify(res.data));
    } finally {
      loading.value = false;
    }
  }

  async function getPerfilCliente(qr_code) {
    const { data } = await api.get(`/cartoes/perfil/${qr_code}`);
    return data;
  }

  async function guardarDesconto(qr_code, desconto) {
    await api.put("/cartoes/desconto", { qr_code, desconto });
  }

  async function iniciarBulkDesconto(qrCodes, desconto) {
    const { data } = await api.post("/bulk/iniciar", { desconto, qrCodes });
    return data;
  }

  // -----------------------------------
  // Filtros / segmentos guardados
  // -----------------------------------
  async function carregarFiltros() {
    const { data } = await api.get("/filtros");
    filtros_utilizador.value = data;
    return data;
  }

  async function guardarFiltro(nome, descricao, filtro) {
    await api.post("/filtros", { nome, filtro, descricao });
    await carregarFiltros();
  }

  async function atualizarFiltro(id, nome, filtro) {
    await api.put(`/filtros/${id}`, { nome, filtro });
  }

  async function apagarFiltro(id) {
    await api.delete(`/filtros/${id}`);
    await carregarFiltros();
  }

  // -----------------------------------
  // Restaurantes
  // -----------------------------------
  async function carregarRestaurantes() {
    const { data } = await api.get("/restaurantes");
    restaurantes.value = data.map((r) => ({ value: r.nome, label: r.nome }));
    return restaurantes.value;
  }

  // -----------------------------------
  // Export CSV
  // -----------------------------------
  function exportarCSV(dados) {
    if (!dados?.length) return false;

    const fields = [
      { label: "QR Code", key: "qr_code" },
      { label: "Nome", key: "nome" },
      { label: "Email", key: "email" },
      { label: "Telemóvel", key: "telefone" },
      { label: "Faixa Etária", key: "faixa_etaria" },
      { label: "Género", key: "sexo" },
      { label: "Total Visitas", key: "visitas_total" },
      { label: "Visitas Parciais", key: "visitas_parciais" },
      { label: "Total Gasto", key: "gasto_total" },
      { label: "Total Parcial", key: "total_parcial" },
      { label: "Ticket Médio", key: "ticket_medio" },
      { label: "Última Visita", key: "ultima_visita" },
      { label: "Penúltima Visita", key: "penultima_visita" },
      { label: "Data Criação", key: "dt_created" },
      { label: "Total Envios", key: "total_envios" },
      { label: "Último Envio", key: "ultima_envio" },
    ];

    const headers = fields.map((f) => f.label);
    const rows = dados.map((obj) =>
      fields.map((f) => `"${(obj[f.key] ?? "").toString().replace(/"/g, '""')}"`).join(";")
    );

    const csvContent = "\uFEFF" + [headers.join(";"), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", "export-clientes.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  }

  return {
    // state
    tableData,
    tableDataOriginal,
    todosProdutos,
    restaurantes,
    filtros_utilizador,
    loading,
    pagination,
    // métodos
    getCartoes,
    aplicarFiltros,
    getPerfilCliente,
    guardarDesconto,
    iniciarBulkDesconto,
    carregarFiltros,
    guardarFiltro,
    atualizarFiltro,
    apagarFiltro,
    carregarRestaurantes,
    exportarCSV,
  };
}
